import React from 'react';
import type { PanelPlacementType, SizeChartAttributeType, SizeChartResultType } from 'src/types/decorationSpecs';
import { getSizeAttributeMm } from 'src/pages/editor/components/decorationSpecs/productPropertySizeChart';
import { formatMmWithInches, formatMm } from 'src/util/units';

export type DecorationSpecsProps = {
    isLoading: boolean;
    decorationPlacement: PanelPlacementType[] | null;
    printWhite: boolean | null;
    /** `null` means no size chart data is available for this product/selection — see
     * `productPropertySizeChart.ts` for how this is resolved (no longer from a separate fetch, so
     * there's no corresponding "ambiguous — missing a selection" state the way
     * `decorationMissingSelections` has; a cell whose value can't be resolved yet just renders as
     * "—", handled per-cell in `formatSizeChartCell`). */
    sizeChart: SizeChartResultType | null;
    /** Options DIS itself reports as still unresolved (e.g. `Backside`, `Decoration Technology`,
     * or `Size`) via a 400 response — see `disClient.ts`'s `retrieveDesignData`. */
    decorationMissingSelections: string[] | null;
    currentSize?: string;
    /** Name of the surface (e.g. `"Front"`, `"Back"`) the currently selected document is on, so
     * the matching decoration-placement row can be highlighted — see `getActiveSurfaceName.ts`. */
    activeSurfaceName?: string;
};

/** Shown for a section instead of its data/"not available" note when the current selection
 * doesn't pin down every option that section's answer can vary by — e.g. `printWhite` genuinely
 * depends on `Substrate Color`, so answering while it's unset would just be picking one color's
 * answer for every color. */
function AmbiguousSelectionNote({ missing }: { missing: string[] }) {
    return <p className='decoration-specs-note'>Select {missing.join(', ')} above to see this.</p>;
}

function NotAvailableNote() {
    return <p className='decoration-specs-note'>Not available for this product.</p>;
}

/**
 * One small two-column (measurement, value) table per panel, rather than one wide table with a
 * column per measurement — a product usually only has 1-2 panels (Front/Back) but 4 measurements
 * (Distance from Collar, Width, Height, X Offset from Center), so a wide table needs far more
 * horizontal room than this drawer has (see `decoration-specs-table-scroll`'s own comment for what that
 * looked like), while a handful of narrow per-panel tables stacked vertically fits comfortably.
 */
function DecorationPlacementPanelTable({ panel, isActive }: { panel: PanelPlacementType; isActive: boolean }) {
    return (
        <div className={`decoration-specs-panel ${isActive ? 'decoration-specs-panel--active' : ''}`}>
            <h5 className='decoration-specs-table__title'>{panel.panelName}</h5>
            <table className='decoration-specs-table'>
                <thead>
                    <tr>
                        <th>Measurement</th>
                        <th>Value</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Distance from Collar</td>
                        <td>{formatMmWithInches(panel.distanceFromCollarToTopMm)}</td>
                    </tr>
                    <tr>
                        <td>Width</td>
                        <td>{formatMmWithInches(panel.scaledPrintWidthMm)}</td>
                    </tr>
                    <tr>
                        <td>Height</td>
                        <td>{formatMmWithInches(panel.scaledPrintHeightMm)}</td>
                    </tr>
                    <tr>
                        <td>X Offset from Center</td>
                        <td>{formatMmWithInches(panel.xOffsetFromCenterMm)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function DecorationPlacementTable({
    decorationPlacement,
    activeSurfaceName,
}: {
    decorationPlacement: PanelPlacementType[];
    activeSurfaceName?: string;
}) {
    return (
        <>
            {decorationPlacement.map((panel) => (
                <DecorationPlacementPanelTable
                    key={panel.panelName}
                    panel={panel}
                    isActive={panel.panelName === activeSurfaceName}
                />
            ))}
        </>
    );
}

/** Every size key present across a table's attributes, in first-seen order — normally every
 * attribute shares the same size keys, but this tolerates one that doesn't rather than dropping
 * data. */
function collectSizes(attributes: SizeChartAttributeType[]): string[] {
    const sizes: string[] = [];
    attributes.forEach((attribute) => {
        Object.keys(attribute.values).forEach((size) => {
            if (!sizes.includes(size)) {
                sizes.push(size);
            }
        });
    });
    return sizes;
}

/** Renders one size-chart cell as `<native value> (<mm equivalent>)`, e.g. `48 cm (480.0 mm)` or
 * `21 in (533.4 mm)` — mm is always the second unit shown (rather than "whichever unit isn't
 * native") so it lines up with decoration placement's mm-native values elsewhere on this same
 * panel, letting a designer cross-reference either section without converting by hand. */
function formatSizeChartCell(attribute: SizeChartAttributeType, size: string): string {
    const raw = attribute.values[size];
    if (raw === undefined) {
        return '—';
    }

    const alreadyHasUnitSuffix = /[a-zA-Z]/.test(raw);
    const primary = !alreadyHasUnitSuffix && attribute.unit ? `${raw} ${attribute.unit}` : raw;

    const mm = getSizeAttributeMm(attribute, size);
    if (mm === undefined) {
        return primary;
    }

    // If the native value is already specified in mm, avoid rendering redundant redundant "(425.0 mm)"
    const normalizedUnit = (
        alreadyHasUnitSuffix ? String(raw).replace(/[\d.\s]/g, '') : attribute.unit || ''
    ).toLowerCase();

    if (normalizedUnit === 'mm') {
        return primary;
    }

    return `${primary} (${formatMm(mm)})`;
}

/**
 * Sizes as rows, measurements as columns — swapped from the more conventional "sizes as columns"
 * print size-chart layout because this table lives in a narrow, fixed-width drawer (see
 * `decorationSpecsDrawer.tsx`): a product typically has more sizes (XS–4XL, say 7-8) than measurement
 * attributes (Length/Width/Sleeve, say 2-4), so rows-per-size scales far better in a narrow column
 * than one column per size would.
 */
function SizeChartTable({ attributes, currentSize }: { attributes: SizeChartAttributeType[]; currentSize?: string }) {
    const sizes = collectSizes(attributes);

    return (
        <div className='decoration-specs-table-scroll'>
            <table className='decoration-specs-table'>
                <thead>
                    <tr>
                        <th>Size</th>
                        {attributes.map((attribute) => (
                            <th key={attribute.attribute}>{attribute.attribute}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sizes.map((size) => (
                        <tr
                            key={size}
                            className={size === currentSize ? 'decoration-specs-table__row--active' : undefined}>
                            <td>{size}</td>
                            {attributes.map((attribute) => (
                                <td key={attribute.attribute}>{formatSizeChartCell(attribute, size)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Displays apparel decoration-placement dimensions, white-ink state, and size chart data for the
 * currently selected product/variables — decoration placement/white-ink from the Design
 * Initialization Service, size chart resolved locally from the SKU picker's own product data (see
 * `productPropertySizeChart.ts`).
 *
 * Size Chart has its own, independent loading state (see this component's own `isLoading`
 * handling below) — it resolves synchronously alongside the SKU picker rather than via the async
 * fetch decoration placement/white ink depend on, so it isn't gated on that fetch's `isLoading` at
 * all. Decoration Placement/White Ink each show one of: a loading note, an "ambiguous — select
 * more options" prompt (when the current selection doesn't pin down every option that section's
 * answer can vary by), its data, or a "not available" note. Renders nothing at all for products
 * with no data, nothing ambiguous, and nothing loading (typically non-apparel) — there's no
 * product-type flag in Scene Maker to gate on, so absence of data is the gate.
 */
export default function DecorationSpecs({
    isLoading,
    decorationPlacement,
    printWhite,
    sizeChart,
    decorationMissingSelections,
    currentSize,
    activeSurfaceName,
}: DecorationSpecsProps) {
    const decorationAmbiguous = !!decorationMissingSelections && decorationMissingSelections.length > 0;
    const hasDecorationPlacement = !!decorationPlacement && decorationPlacement.length > 0;
    const hasSizeChart = !!sizeChart && sizeChart.attributes.length > 0;

    // Confirmed absent for this product/selection, nothing ambiguous, and not loading either
    // (typically non-apparel) — nothing to show. `isLoading` only ever covers
    // decorationPlacement/printWhite (see decorationSpecs/slice.ts) — it's checked here too so this
    // doesn't return `null` and skip rendering the loading state itself while a DIS fetch for a
    // product that turns out to have data is still in flight.
    if (!isLoading && !decorationAmbiguous && !hasDecorationPlacement && !hasSizeChart && printWhite === null) {
        return null;
    }

    return (
        <div className='decoration-specs-data'>
            {/* Size Chart first, and rendered independently of `isLoading` — it resolves from the
            SKU picker's own already-loaded data (see productPropertySizeChart.ts), not the DIS
            fetch decoration placement/white ink depend on, so it has nothing to wait on and stays
            in place across a DIS refetch instead of disappearing and reappearing. */}
            <h4>Size Chart</h4>
            {hasSizeChart ? (
                <SizeChartTable attributes={sizeChart!.attributes} currentSize={currentSize} />
            ) : (
                <NotAvailableNote />
            )}

            <h4>Decoration Placement</h4>
            {isLoading ? (
                <p className='decoration-specs-note'>Loading...</p>
            ) : decorationAmbiguous ? (
                <AmbiguousSelectionNote missing={decorationMissingSelections!} />
            ) : hasDecorationPlacement ? (
                <DecorationPlacementTable
                    decorationPlacement={decorationPlacement!}
                    activeSurfaceName={activeSurfaceName}
                />
            ) : (
                <NotAvailableNote />
            )}

            <h4>White Ink</h4>
            {isLoading ? (
                <p className='decoration-specs-note'>Loading...</p>
            ) : decorationAmbiguous ? (
                <AmbiguousSelectionNote missing={decorationMissingSelections!} />
            ) : (
                <p className='decoration-specs-note'>{printWhite === null ? 'Unknown' : printWhite ? 'Yes' : 'No'}</p>
            )}
        </div>
    );
}
